import { textErrorColor } from '@/core/constants/class-names'
import { cn } from '@/core/lib/utils/cn'
import {
  Control,
  FieldPath,
  FieldValues,
  PathValue,
  RegisterOptions,
  useController,
} from 'react-hook-form'

export interface InputFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> extends React.InputHTMLAttributes<HTMLInputElement> {
  name: TName
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>
  defaultValue?: PathValue<TFieldValues, TName>
  rules?: RegisterOptions<TFieldValues, TName>
  className?: string
}

export function withReactHookForm<TComponentProps extends object>(
  Component: React.ComponentType<TComponentProps>,
) {
  return function WrappedComponent<
    TFieldValues extends FieldValues,
    TName extends FieldPath<TFieldValues>,
  >(
    props: InputFieldProps<TFieldValues, TName> &
      Omit<TComponentProps, keyof InputFieldProps<TFieldValues, TName>>,
  ) {
    const { name, control, defaultValue, rules = {}, ...rest } = props

    const {
      field,
      fieldState: { error },
    } = useController({
      name,
      control,
      defaultValue,
      rules,
    })

    return (
      <div className='w-full'>
        <Component error={error} {...(rest as TComponentProps)} {...field} />
        {error ? (
          <p className={cn('w-full text-sm', textErrorColor)}>
            {error.message}
          </p>
        ) : null}
      </div>
    )
  }
}
